/**
 * SuccessStep
 *
 * Final celebration step - redesigned with world-class editorial aesthetics.
 * Inspired by Collins, Pentagram design studios.
 *
 * Design Philosophy:
 * - Elegant celebration without being over the top
 * - Clear path forward
 * - Refined motion that feels premium
 * - Quiet confidence
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/cn';
import {
  CheckCircle2,
  Sparkles,
  Camera,
  Palette,
  Users,
  ArrowRight,
  Rocket,
} from 'lucide-react';
import {
  OnboardingLayout,
  OnboardingStepContainer,
  OnboardingButton,
} from '../OnboardingLayout';
import type { OnboardingStepProps } from '../OnboardingRouter';

// Quick tips with icons
const QUICK_TIPS = [
  {
    icon: Camera,
    title: 'Upload quality photos',
    description: 'Well-lit, clear images produce the best staging results.',
  },
  {
    icon: Palette,
    title: 'Explore design styles',
    description: 'Try different aesthetics to find what resonates with buyers.',
  },
  {
    icon: Users,
    title: 'Collaborate with your team',
    description: 'Share projects and staging results with colleagues.',
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.4,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function SuccessStep({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const { profile } = useAuth();
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // Get user's first name
  const firstName = profile?.full_name?.split(' ')[0] || '';

  // Update window size for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stop confetti after a few seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  // Track analytics on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'onboarding_success_viewed',
        session_id: onboardingData.session_id,
      });
    }
  }, [onboardingData.session_id]);

  const handleGoToDashboard = async () => {
    // Track completion
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'onboarding_completed',
        session_id: onboardingData.session_id,
        has_subscription: true,
      });
    }

    // Complete onboarding
    await onStepComplete('success', {
      completed_at: new Date().toISOString(),
    });
  };

  return (
    <OnboardingLayout showProgress={false} showSkip={false}>
      {/* Confetti celebration - subtle colors matching brand */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={150}
          gravity={0.2}
          colors={['#7C8C6C', '#D4C5B5', '#E8E4DC', '#A8A29E', '#78716c']}
        />
      )}

      <OnboardingStepContainer maxWidth="2xl">
        {/* Success icon with elegant animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: 'spring',
            duration: 0.8,
            bounce: 0.4,
          }}
          className="text-center mb-10"
        >
          <div className="size-24 bg-olive/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              <CheckCircle2 className="size-12 text-olive" />
            </motion.div>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-charcoal mb-4 text-balance"
          >
            {firstName ? `Welcome, ${firstName}` : 'Welcome to the studio'}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-sand font-sans text-lg sm:text-xl max-w-md mx-auto text-pretty"
          >
            Your account is ready. Let's create something beautiful.
          </motion.p>
        </motion.div>

        {/* Subscription confirmation */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="bg-white border border-stone/20 rounded-3xl p-6 mb-10 text-center shadow-soft"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Sparkles className="size-5 text-olive" />
            <span className="text-sm font-sans font-medium text-olive uppercase tracking-wider">
              Active Subscription
            </span>
          </div>
          <p className="text-charcoal font-sans text-lg">
            Full access to all VirtualStagePro features.
          </p>
          <p className="text-sand font-sans text-sm mt-1">
            Stage unlimited rooms starting today.
          </p>
        </motion.div>

        {/* Quick tips */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-12"
        >
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center font-serif text-xl text-charcoal mb-6"
          >
            Quick tips to get started
          </motion.h2>

          <div className="space-y-4">
            {QUICK_TIPS.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <motion.div
                  key={tip.title}
                  variants={itemVariants}
                  className="group flex items-start gap-4 p-5 bg-white rounded-2xl border border-stone/20 hover:shadow-soft transition-shadow duration-300"
                >
                  <div className="size-12 bg-olive/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-olive/20 transition-colors duration-300">
                    <Icon className="size-6 text-olive" />
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-charcoal mb-1">{tip.title}</h3>
                    <p className="text-sm text-sand leading-relaxed">{tip.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="text-center"
        >
          <OnboardingButton onClick={handleGoToDashboard} size="lg">
            <Rocket className="size-5" />
            Go to Dashboard
            <ArrowRight className="size-5" />
          </OnboardingButton>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-xs text-stone font-sans mt-6"
          >
            Stage your first room directly from the dashboard
          </motion.p>
        </motion.div>
      </OnboardingStepContainer>
    </OnboardingLayout>
  );
}
